import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { File as FileIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { resolveRemotePath } from '../../utils/remotePath';
import { errText, joinRemote } from './ftpViewHelpers';

/* ── Compare a local file with a remote one ────────────────────────────── */

/**
 * "Which remote file do you want to compare this with?" — the files in the
 * remote pane's current directory, with a same-named file preselected when
 * there is one (the common case: a local copy of a server file), plus a path
 * field for any other file on the server.
 *
 * The remote file is read BEFORE the editor opens, so a file that is too large,
 * not text, or missing is reported here, in the dialog where the choice was
 * made, instead of opening an editor that then fails.
 */
export function RemoteComparePicker({ localName, remotePath, remoteFiles, onClose, onPick }) {
  const { t } = useTranslation();
  const sameName = remoteFiles.find((e) => e.name === localName);
  const [chosen, setChosen] = useState(
    sameName ? joinRemote(remotePath, sameName.name) : ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ONE guarded path for the Compare button and a double-click. The double-click
  // used to call `onPick` directly with no busy flag, so a quick second
  // double-click read the remote file twice and opened the editor twice.
  const busyRef = useRef(false);
  const go = async (typed) => {
    if (!String(typed || '').trim() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await onPick(resolveRemotePath(remotePath, typed));
    } catch (err) {
      setError(errText(err));
      busyRef.current = false;
      setBusy(false);
    }
  };
  const submit = (e) => {
    e?.preventDefault?.();
    go(chosen);
  };

  return (
    <Modal isOpen onClose={onClose} title={t('ftp.compareWithRemote')}>
      <form onSubmit={submit} className="space-y-3">
        <p className="ftp-delete-question">{t('ftp.comparePickPrompt', { name: localName })}</p>
        {remoteFiles.length > 0 && (
          <ul className="ftp-delete-list ftp-compare-list" role="listbox">
            {remoteFiles.map((f) => {
              const full = joinRemote(remotePath, f.name);
              return (
                <li key={full}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={chosen === full}
                    className={`ftp-compare-option${chosen === full ? ' ftp-compare-option--on' : ''}`}
                    onClick={() => setChosen(full)}
                    onDoubleClick={() => {
                      setChosen(full);
                      // Double-click is "this one, go": no second click on Compare.
                      go(full);
                    }}
                  >
                    <FileIcon size={12} />
                    <span className="ftp-delete-name">{f.name}</span>
                    {f.name === localName && (
                      <span className="ftp-compare-same">{t('ftp.compareSameName')}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            {t('ftp.compareRemotePath')}
          </label>
          <input
            type="text"
            className="modal-input"
            style={{ width: '100%' }}
            value={chosen}
            placeholder={joinRemote(remotePath, localName)}
            onChange={(e) => setChosen(e.target.value)}
            spellCheck={false}
            autoFocus={!sameName}
          />
        </div>
        {error && <div className="ftp-modal-error">{error}</div>}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!chosen.trim() || busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {busy ? t('ftp.loading') : t('ftp.compare')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
